/**
 * Category ID Utilities
 *
 * Provides consistent ID generation and normalization for category navigation
 * Used across Index.tsx, MeetingSimulator.tsx, and CategorySection.tsx
 */
import { logger } from "@/utils/logger"
/**
 * Generates a normalized category ID from a category name or code
 * Ensures consistent IDs across the application for anchor navigation
 *
 * @param categoryName - The category name or code to normalize
 * @returns A URL-safe, lowercase, hyphenated ID
 *
 * @example
 * generateCategoryId("Daily Team Meetings") // "daily-team-meetings"
 * generateCategoryId("1:1s / Performance") // "1-1s-performance"
 * generateCategoryId("CODE_NAME") // "code-name"
 */
export const generateCategoryId = (categoryName: string): string => {
  return categoryName
    .toLowerCase()
    .trim()
    .replace(/[\/\s_]+/g, "-") // Replace slashes, spaces, and underscores with hyphens
    .replace(/[^a-z0-9-]/g, "") // Remove any non-alphanumeric characters except hyphens
    .replace(/-+/g, "-") // Replace multiple consecutive hyphens with single hyphen
    .replace(/^-|-$/g, ""); // Remove leading/trailing hyphens
};

/**
 * Scrolls to an element with the given category ID
 * Implements retry logic and smooth scrolling behavior
 *
 * @param categoryId - The ID of the category element to scroll to
 * @param options - Scroll behavior options
 * @returns Promise that resolves when scroll is complete or fails
 */
export const scrollToCategoryId = (
  categoryId: string,
  options: {
    maxRetries?: number;
    retryDelay?: number;
    offset?: number;
  } = {}
): Promise<void> => {
  const { maxRetries = 5, retryDelay = 200, offset = 0 } = options;

  return new Promise((resolve) => {
    let attempts = 0;

    const tryScroll = () => {
      attempts++;
      const element = document.getElementById(categoryId);

      if (element) {
        // Calculate scroll position with offset
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;

        // Smooth scroll to element
        window.scrollTo({
          top: offsetPosition,
          behavior: "smooth",
        });

        logger.log(
          `[scrollToCategoryId] Scrolled to #${categoryId} successfully`
        );
        resolve();
      } else if (attempts < maxRetries) {
        logger.log(
          `[scrollToCategoryId] Element #${categoryId} not found, retrying... (${attempts}/${maxRetries})`
        );
        setTimeout(tryScroll, retryDelay);
      } else {
        logger.warn(
          `[scrollToCategoryId] Failed to find element #${categoryId} after ${maxRetries} attempts`
        );
        resolve(); // Resolve anyway to not block execution
      }
    };

    tryScroll();
  });
};
